// Globals
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { AnyObject } from 'antd/es/_util/type';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Table, TableProps } from '@/ui';
import { DragButton, Row } from './components';

export type DraggableTableProps<A> = TableProps<A> & {
  onDragEnd?: (event: DragEndEvent) => void;
};

const DraggableTable = <A extends AnyObject>({
  dataSource = [],
  columns = [],
  rowKey = 'id',
  onDragEnd,
  components = {},
  ...props
}: DraggableTableProps<A>) => {
  const { t } = useTranslation();
  const columnsConfig = useMemo(
    () => [
      {
        id: 'sort',
        key: 'sort',
        align: 'center' as const,
        title: t('label_position'),
        width: 80,
        render: () => <DragButton />,
      },
      ...columns,
    ],
    [columns, t],
  );

  return (
    <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext items={dataSource?.map((i) => i[rowKey.toString()])} strategy={verticalListSortingStrategy}>
        <Table
          components={{
            body: { row: Row },
            ...components,
          }}
          columns={columnsConfig}
          dataSource={dataSource}
          rowKey={rowKey}
          {...props}
        />
      </SortableContext>
    </DndContext>
  );
};

export default DraggableTable;
